/**
 * ScheduleEditor — Sprint 4.7.1
 *
 * Editor visual de agenda (entries[] + exceptions[]). Compartilhado
 * entre TemplateBuilder e DeviceConfigModal/ScheduleTab (D508).
 *
 * Suporta ambos os modos do backend (ScheduleEntryIn):
 *
 *   1. PULSO de reinício — { days, time, duration_s, action }
 *      - action: 'relay_reboot' | 'esp_reboot' (futuro)
 *      - desliga em `time` e religa `duration_s` segundos depois
 *
 *   2. ON/OFF fixo (janela) — { days, on_time, off_time, action }
 *      - action: 'relay_on' | 'relay_off'
 *      - on_time != off_time
 *
 * Em ambos: `days` lista de 0..6 (Dom..Sáb), `enabled` opcional (default true).
 *
 * Props:
 *   - data: { enabled, entries: [...], exceptions: ["YYYY-MM-DD", ...] }
 *   - onChange(patch): recebe { enabled?, entries?, exceptions? }
 *   - showExceptions: bool (default true)
 *
 * Backend é fonte da verdade da validação — esta UI mostra avisos
 * inline mas NÃO bloqueia (o submit caller deve validar antes).
 *
 * Termos técnicos preservados: relay_on, relay_off, relay_reboot,
 * esp_reboot (são valores enviados ao backend).
 */
import { t } from "../../i18n/index.js";
// S2.1 — fonte única: os helpers de agenda agora vivem em scheduleCore.
// Importamos para uso interno e RE-EXPORTAMOS para os importadores atuais
// (DeviceConfigModal, presets, scheduleDiff, SchedulePreviewModal).
import {
  DAY_LABELS,
  DAY_NAMES,
  newOnOffEntry,
  newPulseEntry,
  isPulseEntry,
  isDiscreteSwitchEntry,
  sanitizeEntry,
  validateEntry,
  validateSchedule,
} from "../../lib/scheduleCore.js";

export {
  DAY_LABELS,
  DAY_NAMES,
  newOnOffEntry,
  newPulseEntry,
  isPulseEntry,
  isDiscreteSwitchEntry,
  sanitizeEntry,
  validateEntry,
  validateSchedule,
};

// ACT-2-NOC — entry "baseada em time" (pulso reboot OU discreta on/off):
// decide o sub-formulário time/ação vs o legado de janela on_time/off_time.
const isTimeBasedEntry = (e) => !!(e && e.time !== undefined);

// ============================================================================
// Componente principal
// ============================================================================

// ACT-2-NOC — `allowSwitchActions`: expõe relay_on/relay_off no select de ação
// (eventos DISCRETOS, executados pelo scheduler v2/ACT-1). Default false —
// callers só ligam quando a capability do device reporta multi_event=true
// (DeviceDetail/DeviceConfigModal); TemplateBuilder (fleet-wide) fica fora.
export default function ScheduleEditor({ data, onChange, showExceptions = true, allowSwitchActions = false }) {
  data = data || { enabled: false, entries: [], exceptions: [] };
  const entries = Array.isArray(data.entries) ? data.entries : [];
  const exceptions = Array.isArray(data.exceptions) ? data.exceptions : [];

  const updateEntry = (idx, patch) => {
    const next = entries.map((e, i) => (i === idx ? { ...e, ...patch } : e));
    onChange({ entries: next });
  };
  const removeEntry = (idx) => {
    onChange({ entries: entries.filter((_, i) => i !== idx) });
  };
  const addOnOff = () => {
    onChange({ entries: [...entries, newOnOffEntry()] });
  };
  const addPulse = () => {
    // S2.9 — nova entrada começa em horário PRÓXIMO (agora + 5 min), não no
    // antigo default fixo 04:30 (que parecia uma agenda real já aplicada).
    const d = new Date();
    d.setMinutes(d.getMinutes() + 5);
    const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    onChange({ entries: [...entries, { ...newPulseEntry(), time }] });
  };
  const toggleDay = (idx, day) => {
    const cur = entries[idx]?.days || [];
    const has = cur.includes(day);
    const next = has ? cur.filter((d) => d !== day) : [...cur, day].sort();
    if (next.length === 0) return;
    updateEntry(idx, { days: next });
  };
  const switchToOnOff = (idx) => {
    if (!isPulseEntry(entries[idx])) return;
    updateEntry(idx, {
      on_time: "08:00",
      off_time: "18:00",
      action: "relay_on",
      time: undefined,
      duration_s: undefined,
    });
  };
  const switchToPulse = (idx) => {
    if (isPulseEntry(entries[idx])) return;
    updateEntry(idx, {
      time: "04:30",
      duration_s: 30,
      action: "relay_reboot",
      on_time: undefined,
      off_time: undefined,
    });
  };
  const addException = () => {
    const today = new Date().toISOString().slice(0, 10);
    onChange({ exceptions: [...exceptions, today] });
  };
  const updateException = (idx, value) => {
    onChange({ exceptions: exceptions.map((x, i) => (i === idx ? value : x)) });
  };
  const removeException = (idx) => {
    onChange({ exceptions: exceptions.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-3 text-xs">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={!!data.enabled}
          onChange={(e) => onChange({ enabled: e.target.checked })}
        />
        <span className="font-semibold">{t("schedule.enabled")}</span>
      </label>

      {data.enabled && (
        <>
          {entries.length === 0 && (
            <div className="text-[10px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
              ⚠ {t("templateBuilder.err.scheduleNoEntriesShort", { add: t("templateBuilder.addEntry") })}
            </div>
          )}

          <div className="space-y-2">
            {entries.map((entry, idx) => (
              <div
                key={idx}
                className={`border rounded p-2 space-y-2 ${
                  entry.enabled !== false
                    ? "border-noc-accent/30 bg-noc-bg/30"
                    : "border-noc-border/60 bg-noc-bg/10 opacity-60"
                }`}
              >
                {/* Header da entry: enabled + remove */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-[11px]">
                    <input
                      type="checkbox"
                      checked={entry.enabled !== false}
                      onChange={(e) =>
                        updateEntry(idx, { enabled: e.target.checked })
                      }
                    />
                    <span className="font-semibold text-noc-accent">
                      {t("schedule.entry")} #{idx + 1}
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() => removeEntry(idx)}
                    className="text-[10px] px-2 py-0.5 rounded border border-noc-err/30 text-noc-err hover:bg-noc-err/10"
                    title={t("templateBuilder.removeEntry")}
                  >
                    🗑 {t("common.remove")}
                  </button>
                </div>

                {/* Days of week */}
                <div>
                  <span className="text-noc-muted text-[10px] block mb-1">
                    {t("schedule.days")}
                  </span>
                  <div className="flex gap-1">
                    {DAY_LABELS.map((lbl, day) => {
                      const active = entry.days?.includes(day);
                      return (
                        <button
                          key={day}
                          type="button"
                          onClick={() => toggleDay(idx, day)}
                          title={DAY_NAMES[day]}
                          className={`w-7 h-7 rounded text-[11px] font-mono ${
                            active
                              ? "bg-noc-accent text-noc-bg border border-noc-accent"
                              : "bg-noc-bg border border-noc-border text-noc-muted hover:text-noc-text"
                          }`}
                        >
                          {lbl}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[9px] text-noc-muted/60 mt-1">
                    {entry.days?.length > 0
                      ? entry.days.map((d) => DAY_NAMES[d]).join(", ")
                      : t("templateBuilder.noDaysSelected")}
                  </div>
                </div>

                {/* M1 — janela "Ligar/Desligar fixo" legada oculta da autoria;
                    entradas existentes seguem visíveis (compatibilidade) com
                    aviso + botão de conversão. ACT-2-NOC: liga/desliga agora
                    é autorável como evento DISCRETO (time+action) quando
                    allowSwitchActions=true. */}
                {!isTimeBasedEntry(entry) ? (
                  <div className="text-[10px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded px-2 py-1.5 space-y-1.5">
                    <div>
                      ⚠ "Ligar/Desligar fixo" (janela) é um formato legado —
                      não é aplicado pelo firmware. Converta para manter a
                      agenda efetiva.
                    </div>
                    <button
                      type="button"
                      onClick={() => switchToPulse(idx)}
                      className="px-2 py-1 rounded border border-noc-warn/30 text-noc-warn hover:bg-noc-warn/10"
                    >
                      Converter para Pulso de reinício
                    </button>
                  </div>
                ) : isPulseEntry(entry) ? (
                  <div className="text-[9px] text-noc-muted/60">
                    {t("templateBuilder.pulseHint")}
                  </div>
                ) : null}

                {/* Campos condicionais por modo */}
                {!isTimeBasedEntry(entry) ? (
                  // ==== MODO ON/OFF ====
                  <div className="grid grid-cols-3 gap-2">
                    <label className="space-y-0.5">
                      <span className="text-noc-muted text-[10px]">{t("templateBuilder.onHour")}</span>
                      <input
                        type="time"
                        value={entry.on_time || ""}
                        onChange={(e) => updateEntry(idx, { on_time: e.target.value })}
                        className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text"
                      />
                    </label>
                    <label className="space-y-0.5">
                      <span className="text-noc-muted text-[10px]">{t("templateBuilder.offHour")}</span>
                      <input
                        type="time"
                        value={entry.off_time || ""}
                        onChange={(e) => updateEntry(idx, { off_time: e.target.value })}
                        className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text"
                      />
                    </label>
                    <label className="space-y-0.5">
                      <span className="text-noc-muted text-[10px]">{t("schedule.action")}</span>
                      <select
                        value={entry.action || "relay_on"}
                        onChange={(e) => updateEntry(idx, { action: e.target.value })}
                        className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text"
                      >
                        <option value="relay_on">relay_on</option>
                        <option value="relay_off">relay_off</option>
                      </select>
                    </label>
                  </div>
                ) : (
                  // ==== MODO PULSO / DISCRETO (time + ação) ====
                  <div className="grid grid-cols-3 gap-2">
                    <label className="space-y-0.5">
                      <span className="text-noc-muted text-[10px]">{t("templateBuilder.pulseHour")}</span>
                      <input
                        type="time"
                        value={entry.time || ""}
                        onChange={(e) => updateEntry(idx, { time: e.target.value })}
                        className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text"
                      />
                    </label>
                    {/* ACT-2-NOC — duração só se aplica ao pulso (reboot);
                        eventos discretos relay_on/off não têm duração. */}
                    {!isDiscreteSwitchEntry(entry) && (
                    <label className="space-y-0.5">
                      <span className="text-noc-muted text-[10px]">{t("templateBuilder.pulseDuration")}</span>
                      <input
                        type="number"
                        min={1}
                        max={300}
                        value={entry.duration_s ?? 30}
                        onChange={(e) =>
                          updateEntry(idx, {
                            duration_s: Math.max(1, Math.min(300, Number(e.target.value) || 1)),
                          })
                        }
                        className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text"
                      />
                    </label>
                    )}
                    <label className="space-y-0.5">
                      <span className="text-noc-muted text-[10px]">{t("schedule.action")}</span>
                      <select
                        value={entry.action || "relay_reboot"}
                        onChange={(e) => {
                          const a = e.target.value;
                          // ACT-2-NOC — on/off vira evento DISCRETO (sem
                          // duração); voltar p/ reboot restaura o pulso.
                          if (a === "relay_on" || a === "relay_off") {
                            updateEntry(idx, { action: a, duration_s: undefined });
                          } else {
                            updateEntry(idx, { action: a, duration_s: entry.duration_s ?? 30 });
                          }
                        }}
                        className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text"
                      >
                        <option value="relay_reboot">Reiniciar Relé</option>
                        {/* ACT-2-NOC — liga/desliga discreto, gated por
                            capability do device (multi_event/scheduler v2). */}
                        {allowSwitchActions && (
                          <option value="relay_on">Ligar Relé</option>
                        )}
                        {allowSwitchActions && (
                          <option value="relay_off">Desligar Relé</option>
                        )}
                        {/* sem capability, valor on/off existente segue
                            visível p/ não "sumir" a seleção */}
                        {!allowSwitchActions
                          && (entry.action === "relay_on" || entry.action === "relay_off") && (
                          <option value={entry.action}>{entry.action}</option>
                        )}
                        {/* M1 — esp_reboot só p/ registros legados (firmware
                            executa relay_reboot independentemente). */}
                        {entry.action === "esp_reboot" && (
                          <option value="esp_reboot">esp_reboot (legado)</option>
                        )}
                      </select>
                    </label>
                  </div>
                )}

                {/* ACT-2-NOC — aviso operacional do relay_off agendado */}
                {isTimeBasedEntry(entry) && entry.action === "relay_off" && (
                  <div className="text-[10px] text-noc-err bg-noc-err/10 border border-noc-err/30 rounded px-2 py-1">
                    ⚠ Ao executar, o relé permanece <b>desligado</b> até novo
                    comando ou agenda (o cliente fica sem energia).
                  </div>
                )}

                {/* Validation hints inline */}
                {!isTimeBasedEntry(entry) && entry.on_time === entry.off_time && entry.on_time && (
                  <div className="text-[10px] text-noc-err">
                    ⚠ {t("templateBuilder.err.onOffEqualShort")}
                  </div>
                )}
                {isPulseEntry(entry) && (!entry.time || !entry.duration_s) && (
                  <div className="text-[10px] text-noc-err">
                    ⚠ {t("templateBuilder.err.pulseRequiresFields")}
                  </div>
                )}
                {isPulseEntry(entry) && entry.time && entry.duration_s && (
                  <div className="text-[9px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded px-2 py-1">
                    💡 {t("templateBuilder.pulseExplain", {
                      time: entry.time,
                      duration: entry.duration_s,
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* M1 — "Ligar/Desligar fixo" removido: o firmware atual não executa
              relay_on/relay_off (faz sempre Pulso de reinício). Só expomos a
              opção realmente suportada. */}
          <div className="grid grid-cols-1 gap-1">
            <button
              type="button"
              onClick={addPulse}
              className="px-3 py-1.5 text-[11px] rounded border border-noc-warn/30 text-noc-warn hover:bg-noc-warn/10"
            >
              + {t("templateBuilder.pulseReboot")}
            </button>
            {/* S2.9 — esclarece que o editor é de NOVAS entradas, não a agenda
                real do device (que é mostrada/copiável no card "Agenda real"). */}
            <div className="text-[10px] text-noc-muted/70 italic">
              Nova entrada (horário inicial ≈ agora + 5&nbsp;min — ajuste à vontade).
              Não é a agenda atual do dispositivo.
            </div>
          </div>

          {/* Exceptions */}
          {showExceptions && (
            <div className="pt-2 border-t border-noc-border/50">
              <div className="flex items-center justify-between mb-1">
                <span className="text-noc-muted text-[10px] uppercase tracking-wide">
                  {t("templateBuilder.exceptionsLabel")}
                </span>
                <button
                  type="button"
                  onClick={addException}
                  className="text-[10px] px-2 py-0.5 rounded border border-noc-border text-noc-muted hover:text-noc-text"
                >
                  + {t("common.add").toLowerCase()}
                </button>
              </div>
              {exceptions.length === 0 ? (
                <div className="text-[10px] text-noc-muted/60 italic">
                  {t("templateBuilder.exceptionsEmpty")}
                </div>
              ) : (
                <div className="space-y-1">
                  {exceptions.map((ex, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="date"
                        value={ex || ""}
                        onChange={(e) => updateException(idx, e.target.value)}
                        className="flex-1 bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text text-[11px]"
                      />
                      <button
                        type="button"
                        onClick={() => removeException(idx)}
                        className="text-[10px] px-2 py-0.5 rounded border border-noc-err/30 text-noc-err hover:bg-noc-err/10"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
